// src/components/onboarding/response-retrieval.tsx
//
// The retrieval record under one answer in the per-response drill-down: what
// the engine searched, the results it saw, the pages it opened, every source it
// consulted, and what the answer cites. Same block for every lane; a section a
// lane's API does not report is simply absent. Loaded on demand, one answer at
// a time, because the record lives on the stored answer and a run holds ~1k.
//
import React, { useState } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { getResponseRetrieval, type Retrieval } from "@/api/onboarding-scoring-api";

interface Props {
  customerId: number;
  runId: number;
  responseIndex: number;
}

type Loaded = { retrieval: Retrieval | null; responseText: string };

export function ResponseRetrieval({ customerId, runId, responseIndex }: Props) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded && !loading) {
      setLoading(true);
      setError(null);
      getResponseRetrieval(customerId, runId, responseIndex)
        .then((r) => setLoaded({ retrieval: r.retrieval, responseText: r.response_text }))
        .catch((e) => setError(e instanceof Error ? e.message : String(e)))
        .finally(() => setLoading(false));
    }
  };

  return (
    <div className="mt-2" style={{ fontSize: "var(--text-xs)" }}>
      <button
        onClick={toggle}
        className="flex items-center gap-1"
        style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", color: "var(--signal-blue)", fontWeight: 600, fontSize: "var(--text-xs)" }}
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        Retrieval
      </button>
      {open && (
        <div className="mt-2 pl-4 space-y-2" style={{ borderLeft: "2px solid var(--border)" }}>
          {loading && (
            <span className="flex items-center gap-1" style={{ color: "var(--muted-foreground)" }}>
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
            </span>
          )}
          {error && <span style={{ color: "var(--infrared-coral)" }}>{error}</span>}
          {loaded && !loaded.retrieval && (
            <span style={{ color: "var(--muted-foreground)" }}>
              No retrieval recorded for this answer (web off, nothing retrieved, or collected before retrieval was stored).
            </span>
          )}
          {loaded?.retrieval && <RetrievalBlock retrieval={loaded.retrieval} responseText={loaded.responseText} />}
        </div>
      )}
    </div>
  );
}

function RetrievalBlock({ retrieval, responseText }: { retrieval: Retrieval; responseText: string }) {
  const { queries, results, opens, sources, citations, truncated } = retrieval;
  const queryText = (position: number | null) =>
    position == null ? null : queries.find((q) => q.position === position)?.text ?? null;

  return (
    <>
      {truncated && truncated.length > 0 && (
        <div style={{ color: "var(--chart-3)" }}>Capped at 200 entries: {truncated.join(", ")}</div>
      )}

      {queries.length > 0 && (
        <Section title={`Searches (${queries.length})`}>
          {queries.map((q) => (
            <li key={q.position}>{q.text}</li>
          ))}
        </Section>
      )}

      {results.length > 0 && (
        <Section title={`Results seen (${results.length})`}>
          {results.map((r, i) => (
            <li key={i}>
              <span style={muted}>#{r.rank}</span> <Link url={r.url} title={r.title} />
              {r.page_age && <span style={muted}> · {r.page_age}</span>}
              {queryText(r.query_position) && <span style={muted}> · for “{queryText(r.query_position)}”</span>}
              {r.source_url && r.source_url === r.url && <span style={muted}> · redirect not resolved</span>}
            </li>
          ))}
        </Section>
      )}

      {opens.length > 0 && (
        <Section title={`Pages opened (${opens.length})`}>
          {opens.map((o) => (
            <li key={o.position}><Link url={o.url} title={null} /></li>
          ))}
        </Section>
      )}

      {sources.length > 0 && (
        <Section title={`Sources consulted (${sources.length})`}>
          {sources.map((s) => (
            <li key={s.url}><Link url={s.url} title={s.title} /></li>
          ))}
        </Section>
      )}

      {citations.length > 0 && (
        <Section title={`Citations (${citations.length})`}>
          {citations.map((c) => {
            const span = c.start != null && c.end != null ? responseText.slice(c.start, c.end) : "";
            return (
              <li key={c.position}>
                <Link url={c.url} title={c.title} />
                {span && <div style={quote}>Answer: “{span}”</div>}
                {c.cited_text && <div style={quote}>Source: “{c.cited_text}”</div>}
              </li>
            );
          })}
        </Section>
      )}
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontWeight: 600, color: "var(--muted-foreground)", marginBottom: 2 }}>{title}</div>
      <ol className="space-y-0.5" style={{ listStyle: "none", padding: 0, margin: 0, lineHeight: 1.5 }}>
        {children}
      </ol>
    </div>
  );
}

function Link({ url, title }: { url: string; title: string | null }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--signal-blue)", wordBreak: "break-all" }} title={url}>
      {title || url}
    </a>
  );
}

const muted: React.CSSProperties = { color: "var(--muted-foreground)" };
const quote: React.CSSProperties = { color: "var(--foreground)", paddingLeft: 12, opacity: 0.85 };
